import React, { useState, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import KrgDataTable from "@kargo/shared-components.krg-data-table";
import KrgCheckbox from "@kargo/shared-components.krg-checkbox";
import { makeStyles } from "@mui/styles";
import useVideos, { Query } from "../../hooks/useVideos";
import SearchInput from "../SearchInput";
import CategoriesSelect from "../Select/CategoriesSelect";
import { DatePicker, DateRange } from "../DatePicker/DatePicker";
import useSnackbar from "../../hooks/useSnackbar";
import TagSelect from "../TagSelect/TagSelect";
import { AutocompleteProps } from "../../@types/media.types";
import { Video } from "../../api/types";
import { isEnableEditDelete } from "../../helpers/videos";
import ContentModal from "./ContentModal";
import MediaLoader from "../../pages/media-gallery/MediaLoader";
import { THUMBNAIL_STYLE } from "../../constants/constants";

const useStyles = makeStyles({
  table: {
    "& thead th": {
      background: "transparent",
      color: "black",
      fontSize: "11px",
      fontWeight: 600,
    },
    "& tbody": {
      borderTop: "2px solid black",
      borderBottom: "2px solid black",
      "& tr:hover": {
        background: "#EEF3F7",
      },
    },
  },
  container: {
    maxHeight: "400px",
    overflowY: "auto",
  },
});

type AddVideosModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddVideos: (selectedVideos: Video[]) => void;
  publisherSlug?: string;
  asAdmin?: boolean;
  currentSelectedVideos?: Video[]; // Pass current selections
};

export default function AddVideosModal({
  isOpen,
  onClose,
  onAddVideos,
  publisherSlug,
  asAdmin,
  currentSelectedVideos = [],
}: AddVideosModalProps) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<
    AutocompleteProps[]
  >([]);
  const [selectedTags, setSelectedTags] = useState<AutocompleteProps[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedVideos, setSelectedVideos] = useState<Video[]>([]);
  const { snackbarWarning } = useSnackbar();
  const [pageSize, setPageSize] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);
  const query: Query = {
    limit: pageSize,
    ...(search.length >= 3 && { text: search }),
    ...(selectedCategories.length > 0 && {
      category: selectedCategories.map((category) =>
        category.name.toLowerCase(),
      ),
    }),
    ...(selectedTags.length > 0 && {
      tags: selectedTags.map((tag) => tag.name.toLowerCase()),
    }),
    ...(publisherSlug && { slug: publisherSlug }),
    ...(dateRange &&
      dateRange.to &&
      dateRange.from && { date_range: dateRange }),
  };

  const {
    videos,
    query: videosQuery,
    totalCount,
    hasNextPage,
    fetchNextPage,
  } = useVideos(query);

  // Check if all selectable videos are selected
  const isAllChecked = useMemo(() => {
    const selectedVideosSet = new Set(selectedVideos.map((v) => v.token));
    const pageVideos = videos.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );
    return (
      selectedVideosSet.size > 0 &&
      pageVideos.every(
        (video) =>
          selectedVideosSet.has(video.token) ||
          !isEnableEditDelete(video.state) ||
          currentSelectedVideos.some(
            (selected) => selected.token === video.token,
          ),
      )
    );
  }, [videos, selectedVideos]);

  // Handle toggling selection for all videos
  const handleToggleAll = (checked: boolean) => {
    const pageVideos = videos.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );
    if (!checked) {
      const visibleTokens = new Set(pageVideos.map((v) => v.token));
      setSelectedVideos((prev) =>
        prev.filter((v) => !visibleTokens.has(v.token)),
      );
      return;
    }

    if (pageVideos.length === 0) {
      snackbarWarning("The Video Library is empty.");
      return;
    }

    const tempSelectedVideos = pageVideos.filter(
      (video) =>
        isEnableEditDelete(video.state) &&
        !currentSelectedVideos.find((v) => v.token === video.token),
    );

    if (tempSelectedVideos.length === 0) {
      snackbarWarning("No videos with an active or draft state.");
      return;
    }

    const updatedSelected = [
      ...selectedVideos,
      ...tempSelectedVideos.filter(
        (v) => !selectedVideos.some((sv) => sv.token === v.token),
      ),
    ];
    setSelectedVideos(updatedSelected);
  };

  // Handle toggling selection for a single video
  const handleVideoToggle = (selectedVideo: Video, isSelected: boolean) => {
    if (isSelected) {
      setSelectedVideos([...selectedVideos, selectedVideo]);
    } else {
      setSelectedVideos(
        selectedVideos.filter((video) => video.token !== selectedVideo.token),
      );
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleCategories = (categories: AutocompleteProps[]) => {
    setSelectedCategories(categories);
  };

  const handleTags = (tags: AutocompleteProps[]) => {
    setSelectedTags(tags);
  };

  const handleDateChange = (range: DateRange | undefined) => {
    setDateRange(range);
  };

  const handlePaginationChange = (page: number, currentPageSize: number) => {
    setPageSize(currentPageSize);
    setCurrentPage(page);
    if (page * currentPageSize > videos.length && hasNextPage) {
      fetchNextPage();
    }
  };
  const handleAddVideos = () => {
    onAddVideos(selectedVideos);
    setSelectedVideos([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedVideos([]);
    onClose();
  };

  return (
    <ContentModal
      title="Add Videos"
      isOpen={isOpen}
      style={{ height: "auto", minHeight: "80vh" }}
      onClose={handleClose}
      submitButtonText="ADD"
      cancelButtonText="CANCEL"
      onSubmit={handleAddVideos}
      isSubmitButtonEnabled={selectedVideos.length > 0}
      content={
        <Box>
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Browse your available videos and select the ones you&apos;d like
              to include in your custom playlist.
            </Typography>
          </Box>
          {/* Search and Filter Section */}
          <Grid
            container
            spacing={2}
            justifyContent="flex-start"
            sx={{ marginBottom: 2 }}
          >
            <Grid item xs={12} md="auto">
              <SearchInput
                placeholder="Search"
                text={search}
                onTextChange={handleSearch}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <CategoriesSelect
                value={selectedCategories}
                onChange={handleCategories}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <TagSelect
                asAdmin={asAdmin}
                value={selectedTags}
                onChange={handleTags}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <DatePicker
                onDateRangeChange={handleDateChange}
                value={dateRange}
              />
            </Grid>
          </Grid>

          {/* Videos Table */}
          {videosQuery.isFetched ? (
            <Box sx={{ border: "1px solid #91A0AD" }}>
              <KrgDataTable
                theme={KrgDataTable.THEME_ENUM.v2}
                containerClassName={classes.container}
                className={classes.table}
                config={{
                  data: {
                    columns: [
                      {
                        headerName: "",
                        type: KrgDataTable.CELL_TYPE_ENUM.checkbox,
                        field: "isChecked",
                        renderHeader: () => (
                          <KrgCheckbox
                            theme={KrgCheckbox.THEME_ENUM.v2}
                            isChecked={isAllChecked}
                            onToggle={handleToggleAll}
                          />
                        ),
                        renderCell: (row) => (
                          <KrgCheckbox
                            theme={KrgCheckbox.THEME_ENUM.v2}
                            isChecked={row.isChecked}
                            isEnabled={
                              isEnableEditDelete(row.status) && row.isEnabled
                            }
                            onToggle={(isChecked) => {
                              const video = videos.find(
                                (v) => v.token === row.id,
                              );
                              if (video) {
                                handleVideoToggle(video, isChecked);
                              }
                            }}
                          />
                        ),
                        width: "50px",
                      },
                      {
                        headerName: "Thumbnail",
                        type: KrgDataTable.CELL_TYPE_ENUM.thumbnail,
                        field: "thumbnail",
                        width: "100px",
                        renderCell: (row) => {
                          const thumbnailUrl =
                            typeof row.thumbnail === "string"
                              ? row.thumbnail
                              : "";
                          return (
                            <img
                              src={thumbnailUrl}
                              alt="Video thumbnail"
                              style={THUMBNAIL_STYLE}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          );
                        },
                      },
                      {
                        headerName: "Title",
                        field: "title",
                        width: "200px",
                      },
                      {
                        headerName: "Status",
                        field: "status",
                        width: "100px",
                      },
                      {
                        headerName: "Category",
                        field: "category",
                        width: "120px",
                      },
                      {
                        headerName: "Tags",
                        field: "tags",
                        width: "200px",
                      },
                      {
                        headerName: "Date Uploaded",
                        field: "dateUploaded",
                        width: "120px",
                      },
                    ],
                    rows: videos.map((video) => {
                      const videoChecked = selectedVideos.some(
                        (selected) => selected.token === video.token,
                      );
                      const existingVideo = currentSelectedVideos.some(
                        (selected) => selected.token === video.token,
                      );
                      return {
                        id: video.token,
                        isChecked: videoChecked || existingVideo,
                        isEnabled: !existingVideo,
                        thumbnail: video?.default_stream?.image_url,
                        title: video.title,
                        status: video.state,
                        category: video.category,
                        tags: video.tags?.join(", "),
                        dateUploaded: new Date(
                          video.created_at,
                        ).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        }),
                      };
                    }),
                  },
                  pagination: {
                    initialPageSize: pageSize,
                    totalRowCount: totalCount,
                    hasStrictRows: false,
                  },
                  methods: {
                    onPaginationChange: handlePaginationChange,
                  },
                }}
              />
            </Box>
          ) : (
            <MediaLoader height="auto" />
          )}

          {/* Selected Videos Counter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 2,
              paddingTop: 2,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Selected Videos: {selectedVideos.length}
            </Typography>
          </Box>
        </Box>
      }
    />
  );
}
